import { ChevronLeft } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { TaskService } from '../../Service/TaskService';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { createNotification } from '../../store/notifications/notification-slice';
import { Loading } from '../../components/Loading';
import { Plus } from 'phosphor-react';
import TrashIcon from '../../assets/Icons/trash';
import { QuestionInput } from '../../components/QuestionInput';

const useQuery = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

const TaskDetails = () => {
  const { taskId } = useParams();
  const appDispatch = useAppDispatch();
  const stepId = useQuery().get('step');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [task, setTask] = useState<any>(null);

  const userState = useAppSelector(state => state.user);
  const isAdmin = userState.user?.role !== 'user';

  useEffect(() => {
    getTask();
  }, []);

  const getTask = async () => {
    setIsLoading(true);
    try {
      const { data } = await TaskService.getSingleTask(taskId!);

      setTask(data);
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const submitAnswer = async (body: { answer: any; questionId: string }) => {
    try {
      const { data } = await TaskService.submitAnswer(body);

      const newTask = structuredClone(task);

      const questions = [];
      for (const step of newTask.steps) {
        questions.push(...step.questions);

        for (const questionsGroup of step.questionsGroups) {
          questions.push(...questionsGroup.questions);
        }
      }

      const newQuestion = questions.find(quest => quest.id === body.questionId);
      newQuestion.answer = data;

      setTask(newTask);
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
    }
  };

  const duplicateQuestionsGroup = async (sectionId: string) => {
    try {
      const { data } = await TaskService.duplicateQuestionsGroup(sectionId);

      const newTask = structuredClone(task);

      for (const step of newTask.steps) {
        if (step.questionsGroups[0]?.id === sectionId) {
          step.questionsGroups.push(data);
        }
      }

      setTask(newTask);
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
    }
  };

  const removeQuestionsGroup = async (id: string) => {
    try {
      await TaskService.removeQuestionsGroup(id);
      const newTask = structuredClone(task);

      for (const step of newTask.steps) {
        step.questionsGroups = step.questionsGroups.filter(
          (group: any) => group.id !== id
        );
      }

      setTask(newTask);
      appDispatch(
        createNotification({
          type: 'success',
          message: 'Questions removed successfully!',
        })
      );
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
    }
  };

  const step = useMemo(() => {
    const defaultValue = { name: '', questions: [], questionsGroups: [] };

    if (!task) {
      return defaultValue;
    }

    if (!stepId) {
      const firstStep = task.steps[0];
      navigate(`/tasks/${taskId}?step=${firstStep.id}`);

      return defaultValue;
    } else {
      return task.steps.find((step: any) => step.id === stepId);
    }
  }, [task, stepId]);

  const questionsGroupSection = useMemo(() => {
    const section: any = {};

    for (const group of step.questionsGroups) {
      const id = group.referencedGroupId || group.id;
      if (!section.hasOwnProperty(id)) {
        section[id] = [];
      }

      section[id].push(group);
    }

    return Object.values(section);
  }, [step]);

  const Tab = ({ selected = false, padding, onClick, children }: any) => {
    const styles: any = {};

    if (selected) {
      styles.color = '#1F76C7';
      styles.backgroundColor = '#D9ECFD';
    } else {
      styles.color = '#655C68';
      styles.backgroundColor = '#F4F2F4';
    }

    return (
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          textAlign: 'center',
          borderRadius: '3px',
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '17px',
          padding: padding || '8px',
          cursor: 'pointer',
          ...styles,
        }}
        onClick={onClick}
      >
        {children}
      </Box>
    );
  };

  return (
    <Loading isLoading={isLoading}>
      {!!task && (
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '36px',
            padding: '24px 36px',
            minHeight: '100vh',
          }}
        >
          <Box sx={{ display: 'flex' }}>
            <Tab padding='4px 8px' onClick={() => navigate('/tasks')}>
              <ChevronLeft sx={{ width: '16px', color: '#655C68' }} />
              Back to tasks
            </Tab>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <Typography
              sx={{
                color: '#091E42',
                fontSize: '20px',
                fontWeight: '600',
                lineHeight: '32px',
                marginBottom: '8px',
              }}
            >
              {task.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: '16px' }}>
              {task.steps.map((step: any) => {
                return (
                  <Tab
                    selected={step.id === stepId}
                    onClick={() => navigate(`/tasks/${taskId}?step=${step.id}`)}
                  >
                    {step.name}
                  </Tab>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <Typography
              sx={{
                color: '#091E42',
                fontSize: '20px',
                fontWeight: '600',
                lineHeight: '32px',
                marginBottom: '8px',
              }}
            >
              {step.name}
            </Typography>

            {!!step.questions.length && (
              <Box sx={{ border: '1px solid #E3DDE6' }}>
                {step.questions.map((question: any, index: number) => {
                  return (
                    <QuestionInput
                      key={question.id}
                      title={question.title}
                      question={question}
                      isLastQuestion={index >= step.questions.length - 1}
                      onSubmit={value =>
                        submitAnswer({
                          answer: value,
                          questionId: question.id,
                        })
                      }
                    />
                  );
                })}
              </Box>
            )}

            {questionsGroupSection.map((section: any) => {
              const sectionId = section[0].id;
              const duplicable = section[0].duplicable;

              return (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                  }}
                >
                  {section.map((questionsGroup: any, groupIndex: number) => {
                    return (
                      <Box
                        key={questionsGroup.id}
                        sx={{
                          border: '1px solid #E3DDE6',
                          backgroundColor: 'white',
                          padding: !!duplicable ? '16px' : 0,
                          borderRadius: '4px',
                          boxShadow: '0px 2px 8px 0px #00000014',
                        }}
                      >
                        {!!duplicable && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              sx={{ fontSize: '16px', fontWeight: 'bold' }}
                            >
                              {questionsGroup.name} {groupIndex + 1}
                            </Typography>
                            {!!questionsGroup.referencedGroupId && (
                              <IconButton
                                onClick={() =>
                                  removeQuestionsGroup(questionsGroup.id)
                                }
                              >
                                <TrashIcon size={20} color='#CC7474' />
                              </IconButton>
                            )}
                          </Box>
                        )}
                        {questionsGroup.questions.map(
                          (question: any, index: number) => {
                            return (
                              <QuestionInput
                                key={question.id}
                                title={question.title}
                                question={question}
                                isLastQuestion={
                                  index >= questionsGroup.questions.length - 1
                                }
                                boxShadow={false}
                                onSubmit={value =>
                                  submitAnswer({
                                    answer: value,
                                    questionId: question.id,
                                  })
                                }
                              />
                            );
                          }
                        )}
                      </Box>
                    );
                  })}
                  {!!duplicable && !isAdmin && (
                    <Button
                      variant='contained'
                      sx={{ marginTop: '36px' }}
                      onClick={() => duplicateQuestionsGroup(sectionId)}
                    >
                      <Plus />
                      <span style={{ marginLeft: '5px' }}>Add Another</span>
                    </Button>
                  )}
                </Box>
              );
            })}
          </Box>
        </Stack>
      )}
    </Loading>
  );
};

export default TaskDetails;
